import { LiveAnnouncer } from '@angular/cdk/a11y';
import { computed, DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { L10nTranslationService } from 'angular-l10n';
import { SnackbarService } from '../../snackbar.service';
import { DashboardWidgetRegistry } from '../widget/dashboard-widget-registry';
import { DashboardLayoutEdit, DashboardLayoutEditResult, DashboardLayouts } from '../layout/dashboard-layouts';
import { DashboardLayout } from '../layout/dashboard-layout';
import { DashboardView } from '../views/dashboard-view';
import { UNAVAILABLE_WIDGET_TITLE } from '../widget/dashboard-widget-definition';
import { DashboardViewOutcome, DashboardViews } from '../views/dashboard-views';
import { describeEdit, describeViewOutcome, isViewFailure } from './dashboard-announcements';

interface Draft {
  /** The saved view being edited; null when editing the Default view */
  base: DashboardView | null;
  layout: DashboardLayout;
}

/** Customize mode: a draft of the shown view, edited until saved or cancelled. */
@Injectable()
export class DashboardCustomizeSession {
  private readonly views = inject(DashboardViews);
  private readonly layouts = inject(DashboardLayouts);
  private readonly registry = inject(DashboardWidgetRegistry);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translation = inject(L10nTranslationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly draft = signal<Draft | null>(null);

  readonly editing = computed(() => this.draft() !== null);
  /** Null outside Customize mode or on the Default view */
  readonly editedView: Signal<DashboardView | null> = computed(() => this.draft()?.base ?? null);
  /** The draft while editing, otherwise the shown view; null until the views are loaded */
  readonly layout = computed(() => this.draft()?.layout ?? this.views.shownLayout());
  readonly visibleWidgets = computed(() => {
    const layout = this.layout();
    return layout ? this.layouts.visibleWidgets(layout) : [];
  });

  constructor() {
    // Edits that arrive after the dashboard is gone find no draft
    this.destroyRef.onDestroy(() => this.draft.set(null));
  }

  begin(): void {
    if (!this.draft()) {
      this.draftShownView();
    }
  }

  /** Restarts the draft on the picked view; null picks the Default view */
  async switchView(id: string | null): Promise<void> {
    if (!this.draft()) {
      return;
    }

    const outcome = await this.views.select(id);
    if (outcome?.kind === 'shown') {
      this.draftShownView();
    }

    if (outcome) {
      this.report(outcome);
    }
  }

  cancel(): void {
    this.draft.set(null);
  }

  /** Rejected with 'not-found' outside Customize mode */
  apply(edit: DashboardLayoutEdit): DashboardLayoutEditResult {
    const draft = this.draft();
    if (!draft) {
      return { applied: false, reason: 'not-found' };
    }

    const title = this.titleOf(draft.layout, edit);
    const result = this.layouts.apply(draft.layout, edit);
    if (result.applied) {
      this.draft.set({ ...draft, layout: result.layout });
    }

    const message = describeEdit(edit, result, title, this.translation);
    if ('reason' in result && result.reason === 'invalid-settings') {
      this.alert(message);
    } else {
      this.announce(message);
    }

    return result;
  }

  /** Changes only the draft */
  startFromDefault(): boolean {
    const draft = this.draft();
    const defaultLayout = this.views.defaultLayout();
    if (!draft || !defaultLayout) {
      return false;
    }

    this.draft.set({ ...draft, layout: defaultLayout });
    this.announce(this.translation.translate('Dashboard.Announce.DraftReplaced'));

    return true;
  }

  /** Saves over the edited view, or as a new view from the Default view */
  save(name: string): Promise<void> {
    return this.commit(name, this.editedView()?.id ?? null);
  }

  saveAsNew(name: string): Promise<void> {
    return this.commit(name, null);
  }

  async deleteView(): Promise<void> {
    const edited = this.editedView();
    if (!edited) {
      return;
    }

    const outcome = await this.views.delete(edited.id);
    if (outcome.kind === 'deleted') {
      this.cancel();
    }

    this.report(outcome);
  }

  /** Keeps the draft, so it can be saved once the unreadable views are gone */
  async resetSavedViews(): Promise<void> {
    if (!this.draft()) {
      return;
    }

    const outcome = await this.views.reset();
    if (outcome) {
      this.report(outcome);
    }
  }

  private async commit(name: string, replacing: string | null): Promise<void> {
    const draft = this.draft();
    if (!draft) {
      return;
    }

    const outcome = await this.views.save(draft.layout, name, replacing);
    if (outcome.kind === 'saved') {
      // Edits made while saving stay in the draft, now based on the saved view
      const current = this.draft();
      this.draft.set(current === draft ? null : current && { ...current, base: outcome.view });
    }

    this.report(outcome);
  }

  private draftShownView(): void {
    const layout = this.views.shownLayout();
    if (layout) {
      this.draft.set({ base: this.views.activeView(), layout });
    }
  }

  /** Looked up before the edit can remove the widget */
  private titleOf(layout: DashboardLayout, edit: DashboardLayoutEdit): string {
    let type: string | undefined;
    if ('type' in edit) {
      type = edit.type;
    } else {
      const id = 'id' in edit ? edit.id : keyboardIdOf(edit);
      type = layout.widgets.find((widget) => widget.id === id)?.type;
    }

    return this.translation.translate((type && this.registry.find(type)?.title) || UNAVAILABLE_WIDGET_TITLE);
  }

  private report(outcome: DashboardViewOutcome): void {
    const message = describeViewOutcome(outcome, this.translation);
    if (isViewFailure(outcome)) {
      this.alert(message);
    } else {
      this.announce(message);
    }
  }

  private announce(message: string | null): void {
    if (message) {
      void this.announcer.announce(message, 'polite');
    }
  }

  /** The snack bar is announced too */
  private alert(message: string | null): void {
    if (message) {
      this.snackBar.open(message, this.translation.translate('OK'), SnackbarService.errorConfig());
    }
  }
}

function keyboardIdOf(edit: DashboardLayoutEdit): string | undefined {
  return edit.kind === 'arrange' ? edit.keyboard?.id : undefined;
}
